'use client';

import { useRef, useEffect, useMemo } from 'react';
import { useAiChatStore } from '@/stores/ai-chat-store';
import { AiTextMessage } from './ai-text-message';
import { AiToolCallCard } from './ai-tool-result';
import { AiApprovalCard } from './ai-approval-card';
import { Loader2 } from 'lucide-react';
import { useTranslations } from '@/i18n/useTranslations';
import type { AnthropicContentBlock } from '@comp-sys/shared';

function getToolLabel(name: string, t: (key: string) => string): string {
  const key = `tool.${name}`;
  const label = t(key);
  return label === `ai.${key}` ? name : label;
}

interface AiMessageListProps {
  onApproved?: (stream: ReadableStream<Uint8Array>) => void;
}

export function AiMessageList({ onApproved }: AiMessageListProps) {
  const t = useTranslations('ai');
  const messages = useAiChatStore((s) => s.messages);
  const isStreaming = useAiChatStore((s) => s.isStreaming);
  const streamingBlocks = useAiChatStore((s) => s.streamingBlocks);
  const pendingApprovals = useAiChatStore((s) => s.pendingApprovals);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Build tool_result lookup from all historical messages
  const toolResultMap = useMemo(() => {
    const map = new Map<string, unknown>();
    for (const msg of messages) {
      const blocks = (msg.content as unknown as AnthropicContentBlock[]) || [];
      for (const block of blocks) {
        if (block.type === 'tool_result') {
          try {
            map.set(block.tool_use_id, JSON.parse(block.content));
          } catch {
            if (block.content === 'approval_pending') {
              map.set(block.tool_use_id, { approvalPending: true });
            } else {
              map.set(block.tool_use_id, block.content);
            }
          }
        }
      }
    }
    return map;
  }, [messages]);

  const hasStreamingBlocks = streamingBlocks.length > 0;
  const hasStreamingText = streamingBlocks.some((b) => b.type === 'text');
  const hasContent = messages.length > 0 || isStreaming || hasStreamingBlocks;

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, streamingBlocks]);

  return (
    <div ref={scrollRef} className="flex-1 overflow-auto overscroll-contain px-3 py-2">
      {!hasContent && (
        <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
          {t('readyPlaceholder')}
        </div>
      )}
      {messages.map((msg) => {
        const blocks = (msg.content as unknown as AnthropicContentBlock[]) || [];
        return blocks.map((block, i) => {
          if (block.type === 'text') {
            return <AiTextMessage key={`${msg.id}-${i}`} content={block.text} isUser={msg.role === 'user'} />;
          }
          if (block.type === 'tool_use') {
            const label = getToolLabel(block.name, t);
            const result = toolResultMap.get(block.id);
            return (
              <AiToolCallCard
                key={`${msg.id}-tool-${i}`}
                tc={{
                  id: block.id || `${msg.id}-tool-${i}`,
                  name: block.name,
                  label,
                  status: 'done',
                  result: result ?? null,
                  params: block.input,
                }}
              />
            );
          }
          if (block.type === 'tool_result') {
            return null;
          }
          // Handle thinking/reasoning blocks silently
          return null;
        });
      })}
      {/* Streaming blocks in arrival order */}
      {streamingBlocks.map((block, idx) => {
        if (block.type === 'text') {
          const isLast = idx === streamingBlocks.length - 1;
          return <AiTextMessage key={block.id} content={block.content} isUser={false} showCursor={isStreaming && isLast} />;
        }
        return <AiToolCallCard key={block.id} tc={block} />;
      })}
      {/* Thinking indicator when waiting for LLM first response */}
      {isStreaming && !hasStreamingBlocks && (
        <div className="flex justify-start mb-3">
          <div className="bg-overlay/70 rounded-2xl px-4 py-2.5 border border-surface flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            <span className="text-sm text-muted-foreground">{t('thinking')}</span>
          </div>
        </div>
      )}
      {/* Approval cards */}
      {Object.values(pendingApprovals).map((approval) => (
        <AiApprovalCard key={approval.requestId} approval={approval} onApproved={onApproved} />
      ))}
    </div>
  );
}
